import React, { useState, useMemo, useEffect } from "react";

import { Label as LabelDropdown, Modal, Typography } from "@packages/ds";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, BackLink, Link } from "ds/Button";
import Dialog from "ds/Dialog";
import Loading from "ds/PageLoading";
import Select from "ds/Select";
import * as Table from "ds/Table";
import Tag from "ds/Tag";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import { gitProjectSelector } from "Reducers/project/project";
import { useLoadDomains } from "Reducers/project/settings/domains/hooks/useLoadDomains";
import { useAddEnvironmentDomain } from "Reducers/project/settings/environmentDomains/hooks/useAddEnvironmentDomain";
import CompleteConfigurationInfo from "src/organization/pages/project/environment/settings/domains/components/CompleteConfigurationInfo/CompleteConfigurationInfo";

import * as S from "./AddEnvironmentDomain.styles";

type SelectOption = { value: string; label: string };

export type AddEnvironmentDomainFormDataProps = {
  environmentDomainName: string;
  replacementDomain: string;
  environmentId: string;
};

const AddEnvironmentDomain = () => {
  const environment = useSelectorWithUrlParams(environmentSelector);
  const navigate = useNavigate();

  const [sort] = Table.useSort<{ name: string; created_at: string }>({
    key: "created_at",
    direction: "asc"
  });

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const { domains, loading } = useLoadDomains(project);

  const productionDomains = useMemo(() => {
    if (!domains) return [];
    const key = sort.key || "name";
    const list = [...domains].sort((a, b) => {
      if (sort.direction === "asc") {
        return a[key].localeCompare(b[key]);
      } else {
        return b[key].localeCompare(a[key]);
      }
    });

    return list?.map(domain => ({
      value: domain?.name,
      label: domain?.name
    }));
  }, [domains, sort]);

  const [showAddDomainModal, setShowAddDomainModal] = useState(false);
  const [submittedData, setSubmittedData] = useState(false);

  const [formValues, setFormValues] =
    useState<AddEnvironmentDomainFormDataProps>({
      environmentDomainName: "",
      replacementDomain: "",
      environmentId: ""
    });

  const intl = useIntl();

  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();

  const encodedEnvironmentId = encodeURIComponent(environmentId);

  const { handleSave, errors, status } = useAddEnvironmentDomain(
    environment,
    project
  );

  const onFieldChange = (value, fieldName) => {
    const form = { ...formValues, [fieldName]: value };
    setFormValues(form);
  };

  const onFieldChangeOneEnvironment = value => {
    setFormValues(value);
  };

  const isLoading = false;

  const onEnvironmentDomainNameChange = e => {
    if (productionDomains?.length === 1) {
      onFieldChangeOneEnvironment({
        environmentDomainName: e.target.value,
        replacementDomain: productionDomains[0]?.value
      });
    } else {
      onFieldChange(e.target.value, "environmentDomainName");
    }
  };

  const onReplacementDomainChange = ({ value }: SelectOption) => {
    onFieldChange(value, "replacementDomain");
  };

  const handleModalClose = () => {
    navigate(
      `/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains`
    );
  };

  useEffect(() => {
    if (submittedData && !errors && status !== "pending") {
      setShowAddDomainModal(true);
    }
  }, [submittedData, errors, status]);

  const domainSelection = productionDomains.find(
    domain => domain?.value === formValues?.replacementDomain
  );
  return (
    <S.Layout>
      <S.Container>
        <BackLink
          id="project-domains-back-button"
          from={`/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains/-/add`}
          variant="link"
          to={`/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains`}
          capitalized={false}
          analyticId="domains.back_to"
        >
          {intl.formatMessage({ id: "domains.back_to" })}
        </BackLink>
        <S.Header>{intl.formatMessage({ id: "domains.add_title" })}</S.Header>
        <S.SubHeader>
          {intl.formatMessage({ id: "domains.add_subtitle" })}
        </S.SubHeader>

        <S.AddDomainFormLayout
          id="domain-add-form"
          aria-labelledby="add-domain-heading"
          onSubmit={e => {
            e.preventDefault();

            const addEnvironmentDomainData = {
              name: formValues.environmentDomainName,
              environmentId: encodedEnvironmentId,
              replacement_for: formValues.replacementDomain
            };

            handleSave(addEnvironmentDomainData);
            setSubmittedData(true);
          }}
        >
          <S.Input
            label={capitalize(`${intl.formatMessage({ id: "domain" })}`)}
            placeholder={intl.formatMessage({ id: "domain.input.placeholder" })}
            name="domain"
            id="project-domain-private-key-input"
            required
            onChange={e => {
              onEnvironmentDomainNameChange(e);
            }}
            error={
              errors?.detail?.name || errors?.detail?.error || errors?.message
            }
          />

          <Typography.Label>
            {capitalize(intl.formatMessage({ id: "domains.environment" }))}
          </Typography.Label>
          <S.Text>{environment?.id}</S.Text>

          {loading || !productionDomains ? (
            <Loading />
          ) : productionDomains?.length !== 1 ? (
            <>
              <S.LearnMoreContainer>
                <FormattedMessage
                  id="domains.learn_more"
                  values={{
                    link: txt => (
                      <S.LearnMoreLink
                        analyticId="settings.domains.add.domain.learn_more"
                        href={intl.formatMessage({
                          id: "settings.domains.add.domain.learn_more"
                        })}
                      >
                        {txt}
                      </S.LearnMoreLink>
                    )
                  }}
                />
              </S.LearnMoreContainer>

              <S.SelectWrapper>
                <LabelDropdown required htmlFor="add-domain-from-production">
                  {intl.formatMessage({
                    id: "domains.replacement_domain"
                  })}
                </LabelDropdown>
                <Select
                  id="add-domain-from-production"
                  options={productionDomains}
                  value={domainSelection}
                  onChange={option => {
                    onReplacementDomainChange(option as SelectOption);
                  }}
                  placeholder={intl.formatMessage({
                    id: "domains.select_domain"
                  })}
                />
              </S.SelectWrapper>
            </>
          ) : (
            <>
              <S.AttachedContainer>
                <S.Label>
                  {capitalize(
                    intl.formatMessage({ id: "domains.replacement_domain" })
                  )}
                </S.Label>
                <Dialog
                  analyticId="domains.replacement_domain.info"
                  description={intl.formatMessage({
                    id: "domains.replacement_domain.info",
                    defaultMessage:
                      "For the routing of your app, your production domain maps to this non-production domain."
                  })}
                  link={
                    <Link
                      analyticId="learnmore"
                      href={intl.formatMessage({
                        id: "settings.domains.add.domain.learn_more"
                      })}
                    >
                      {intl.formatMessage({ id: "learnmore" })}
                    </Link>
                  }
                  align="center"
                />
              </S.AttachedContainer>

              <S.ReplacementDomainContainer>
                <S.TextName>{productionDomains[0]?.value}</S.TextName>
                <Tag variant="purple">
                  {capitalize(intl.formatMessage({ id: "default" }))}
                </Tag>
              </S.ReplacementDomainContainer>
            </>
          )}

          {isLoading ? (
            <Loading />
          ) : (
            <S.ButtonWrapper>
              <Button
                analyticId="add"
                id="project-domain-save-btn"
                type="submit"
                disabled={
                  !formValues.environmentDomainName ||
                  !formValues.replacementDomain
                }
              >
                {capitalize(intl.formatMessage({ id: "add" }))}
              </Button>
              <Button
                analyticId="cancel"
                id="project-domain-cancel-btn"
                variant="secondary"
                to={`/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains`}
              >
                {capitalize(intl.formatMessage({ id: "cancel" }))}
              </Button>
            </S.ButtonWrapper>
          )}
        </S.AddDomainFormLayout>
      </S.Container>

      <Modal
        size="medium"
        isOpen={showAddDomainModal}
        onOpenChange={() => {
          setShowAddDomainModal(!showAddDomainModal);
        }}
        id="add-domain-modal"
        handleClose={() => {
          handleModalClose();
        }}
      >
        <CompleteConfigurationInfo
          customDomainName={formValues?.environmentDomainName}
        />
      </Modal>
    </S.Layout>
  );
};

export default AddEnvironmentDomain;
